<template>
  <form class="msg-composer" @submit.prevent="submit">
    <!-- Отвечаю на … — единственное место, где видно, куда уйдёт следующее
         сообщение. Без полоски цитата была бы невидимым состоянием: человек
         нажал «Ответить», отвлёкся, вернулся и написал обычную реплику, а она
         ушла ответом на что-то давнее. -->
    <div v-if="replyTo" class="msg-reply">
      <span class="msg-reply__body">
        <span class="msg-reply__author">Отвечаю: {{ replyAuthor }}</span>
        <span class="msg-reply__text">{{ replyText }}</span>
      </span>
      <button
        type="button"
        class="msg-reply__drop"
        aria-label="Отменить ответ"
        @click="$emit('cancel-reply')"
      >✕</button>
    </div>

    <!-- Черновые файлы: видно, ЧТО уйдёт, до отправки. -->
    <div v-if="drafts.length" class="msg-composer__drafts">
      <div v-for="(item, index) in drafts" :key="item.key" class="msg-draft">
        <span class="msg-draft__name">{{ item.file.name }}</span>
        <span v-if="item.progress < 100" class="msg-draft__bar">
          <span class="msg-draft__bar-fill" :style="{ width: item.progress + '%' }" />
        </span>
        <!-- Пока идёт отправка, снимать нельзя: набор уже уехал в цикл
             загрузки, и «убранный» файл всё равно ушёл бы, а сосед по списку
             потерялся бы молча. -->
        <button
          type="button"
          class="msg-draft__drop"
          aria-label="Убрать файл"
          :disabled="sending"
          @click="dropDraft(index)"
        >✕</button>
      </div>
    </div>

    <!-- Запись голосового: пока идёт, поле ввода не нужно — нечего печатать. -->
    <div v-if="recording" class="msg-composer__recording">
      <span class="msg-composer__dot" />
      <span class="msg-composer__timer">{{ recordClock }}</span>
      <span class="msg-composer__hint">Идёт запись</span>
      <button type="button" class="msg-composer__cancel" @click="cancelRecording">Отменить</button>
      <button type="button" class="msg-composer__stop" @click="stopRecording">Отправить</button>
    </div>

    <div v-else class="msg-composer__row">
      <!-- Кнопка, а не голая метка: метку не поймать табуляцией, и человек,
           который не пользуется мышью, не приложит файл вообще. -->
      <button
        type="button"
        class="msg-composer__attach"
        title="Приложить файл"
        aria-label="Приложить файл"
        @click="$refs.picker.click()"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
        </svg>
      </button>
      <input ref="picker" type="file" multiple hidden @change="pickFiles" />

      <textarea
        ref="input"
        v-model="text"
        class="msg-composer__input"
        rows="1"
        placeholder="Написать сообщение…"
        :maxlength="MAX_TEXT"
        :disabled="sending"
        :aria-label="replyTo ? `Ответ на сообщение: ${replyAuthor}` : 'Текст сообщения'"
        @input="grow"
        @keydown.enter.exact.prevent="submit"
        @keydown.esc="$emit('cancel-reply')"
      />

      <button
        v-if="canRecord && !text && !drafts.length"
        type="button"
        class="msg-composer__mic"
        title="Записать голосовое" aria-label="Записать голосовое"
        @click="startRecording"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 17v5"/>
        </svg>
      </button>

      <button v-else type="submit" class="msg-composer__send" :disabled="!canSend" title="Отправить" aria-label="Отправить">
        <svg v-if="!sending" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/>
        </svg>
        <span v-else class="msg-composer__spinner" />
      </button>
    </div>

    <!-- Счётчик появляется на подходе к пределу, а не висит всегда: пока до
         потолка далеко, он только мешает. -->
    <p v-if="text.length > MAX_TEXT - 200" class="msg-composer__count">
      {{ text.length }} / {{ MAX_TEXT }}
    </p>

    <!-- Отказ виден рядом с текстом, а не всплывашкой: текст остался в поле,
         и человеку решать, нажимать ли ещё раз. -->
    <p v-if="error" class="msg-composer__error" role="alert">{{ error }}</p>
  </form>
</template>

<script>
import messenger from '@/utils/messenger';
import { analyzeVoice } from '@/utils/voice';

/** Ключ идемпотентности: живёт, пока живёт черновик. */
function newKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default {
  name: 'MessageComposer',
  props: {
    chatUuid: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    /**
     * Сообщение, на которое отвечаем, — целиком и ПРОПСОМ.
     *
     * Хозяин цитаты не композитор: выставляет её пузырь, гасит отправка, и
     * общий владелец у них один — экран разговора. Своего состояния здесь быть
     * не должно, иначе полоска и то, что реально уедет, разойдутся.
     */
    replyTo: { type: Object, default: null },
  },
  emits: ['sent', 'cancel-reply'],
  data() {
    return {
      text: '',
      drafts: [],
      sending: false,
      error: '',
      /**
       * КЛЮЧ ПРИНАДЛЕЖИТ ЧЕРНОВИКУ, А НЕ ПОПЫТКЕ.
       *
       * Связь рвётся ровно тогда, когда ответ уже ушёл, но не дошёл. Придумай
       * новый ключ на второе нажатие — и собеседник получит две одинаковые
       * реплики. Гасим только после успеха.
       */
      draftKey: '',
      /**
       * ЧЕМУ ЭТОТ КЛЮЧ БЫЛ ВЫДАН.
       *
       * Ключ спасает от двойной отправки, но ровно того же самого. Человек
       * отправил, получил отказ, ИСПРАВИЛ текст и нажал снова — под старым
       * ключом сервер отвечает прежним сообщением, и правка пропадает молча:
       * в ленте висит то, что человек только что переписал. Изменилось
       * содержимое — значит это другое сообщение, и ключ ему нужен свой.
       */
      draftKeyFor: '',
      /** Потолок текста на стороне сервера. */
      MAX_TEXT: 4000,
      recording: false,
      recorder: null,
      chunks: [],
      recordedFor: 0,
      recordTimer: null,
    };
  },
  computed: {
    canSend() {
      return !this.sending && !this.disabled && (this.text.trim() !== '' || this.drafts.length > 0);
    },
    canRecord() {
      return !!(navigator.mediaDevices && window.MediaRecorder);
    },
    recordClock() {
      const m = Math.floor(this.recordedFor / 60);
      const s = this.recordedFor % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    /**
     * Чьё сообщение цитируем.
     *
     * НАСТОЯЩЕЕ ИМЯ, А НЕ «ВЫ» ДЛЯ СВОЕГО. После отправки цитату рисует пузырь,
     * а он печатает `actor_name` — то есть имя. Скажи здесь «Вы» — и подпись
     * сменилась бы на глазах: «Вы» до отправки, «Айгерим Абдиева» после.
     */
    replyAuthor() {
      return this.replyTo?.meta?.actor?.name || 'сообщение';
    },
    /** Пустой текст — обычное дело: сканы присылают молча. */
    replyText() {
      const text = this.replyTo?.data?.text || '';

      return text || 'вложение';
    },
  },
  methods: {
    grow() {
      const el = this.$refs.input;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    },

    pickFiles(event) {
      const picked = Array.from(event.target.files || []);
      event.target.value = '';

      for (const file of picked) {
        // Предел проверяем ЗДЕСЬ: сервер тоже проверит, но гонять двадцать
        // мегабайт ради отказа — впустую тратить чужой мобильный трафик.
        if (file.size > 20 * 1024 * 1024) {
          this.error = `«${file.name}» больше 20 МБ — столько система не принимает.`;
          continue;
        }
        if (this.drafts.length >= 10) {
          this.error = 'За раз можно приложить не больше 10 файлов.';
          break;
        }
        this.drafts.push({ key: newKey(), file, progress: 0, uuid: null });
      }
    },

    dropDraft(index) {
      this.drafts.splice(index, 1);
    },

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.chunks = [];
        this.recorder = new MediaRecorder(stream);
        this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
        this.recorder.onstop = () => stream.getTracks().forEach((t) => t.stop());
        this.recorder.start();

        this.recording = true;
        this.recordedFor = 0;
        this.recordTimer = setInterval(() => { this.recordedFor += 1; }, 1000);
      } catch {
        this.error = 'Микрофон недоступен — проверьте разрешение в браузере.';
      }
    },

    stopRecording() {
      if (!this.recorder) return;

      const seconds = this.recordedFor;
      const chunks = this.chunks;

      // ДИКТОФОН ДЕРЖИМ В ЛОКАЛЬНОЙ ССЫЛКЕ. `stop()` асинхронный: обработчик
      // сработает уже после того, как состояние компонента обнулят, и обращение
      // к `this.recorder` в нём упадёт на null.
      const recorder = this.recorder;

      recorder.onstop = async () => {
        recorder.stream?.getTracks().forEach((t) => t.stop());

        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const file = new File([blob], 'Голосовое.webm', { type: blob.type });

        // Волну и точную длительность считаем ДО отправки: файл ещё жив как
        // blob, а на сервере из звука их уже не достать. Не вышло — уходит без
        // них, плеер обойдётся.
        const { peaks, duration } = await analyzeVoice(blob);

        // Длительность из декодера точнее секундного таймера (он у записи
        // короче секунды даёт 0); нет декодера — берём таймер, но не меньше 1.
        const durationSec = duration !== null ? Math.max(1, Math.round(duration)) : Math.max(1, seconds);

        // `voice: true` — ГЛАВНОЕ. Без него сервер сниффит webm как video/webm
        // и голосовое приезжает «видео» с дефолтным плеером браузера.
        this.drafts.push({ key: newKey(), file, progress: 0, uuid: null, voice: true, durationSec, waveform: peaks });
        this.finishRecording();
        this.submit();
      };

      recorder.stop();
    },

    cancelRecording() {
      const recorder = this.recorder;

      // Здесь ловушка особенно заметна: `finishRecording()` обнуляет ссылку
      // СРАЗУ, а обработчик остановки приходит позже — микрофон остался бы
      // включённым, и браузер продолжал бы показывать красную точку записи.
      if (recorder) {
        recorder.onstop = () => recorder.stream?.getTracks().forEach((t) => t.stop());
        recorder.stop();
      }

      this.finishRecording();
    },

    finishRecording() {
      clearInterval(this.recordTimer);
      this.recordTimer = null;
      this.recording = false;
      this.recorder = null;
      this.chunks = [];
      this.recordedFor = 0;

      // Поле ввода на время записи исчезало вместе с фокусом — вернём его,
      // иначе следующая клавиша уходит в никуда.
      this.focusInput();
    },

    focusInput() {
      this.$nextTick(() => this.$refs.input?.focus());
    },

    /**
     * Отправка: СНАЧАЛА файлы, потом сообщение.
     *
     * Наоборот было бы хуже: упавшая загрузка оставила бы в ленте «отправляю
     * диплом» без диплома. Не доехал файл — не уходит и сообщение, текст
     * остаётся в поле.
     */
    async submit() {
      if (!this.canSend) return;

      this.sending = true;
      this.error = '';

      // СНИМОК НАБОРА. Дальше идут await'ы, а `drafts` — живой массив: снять
      // файл посреди загрузки значило бы сдвинуть индексы под ногами цикла.
      // Кнопка снятия на это время отключена, снимок — второй рубеж.
      const drafts = this.drafts.slice();
      const uuids = [];

      for (const draft of drafts) {
        // Уже загруженный файл второй раз не грузим: повторное нажатие после
        // отказа не должно гонять мегабайты заново.
        if (draft.uuid) {
          uuids.push(draft.uuid);
          continue;
        }

        const res = await messenger.upload(this.chatUuid, draft.file, {
          voice: draft.voice === true,
          durationSec: draft.durationSec ?? null,
          waveform: draft.waveform ?? null,
          onProgress: (value) => { draft.progress = value; },
        });

        if (!res.ok || !res.data?.attachment_uuid) {
          this.error = res.error || 'Файл не удалось отправить';
          this.sending = false;
          return;
        }

        draft.uuid = res.data.attachment_uuid;
        uuids.push(draft.uuid);
      }

      // Ключ выдаём ПОД СОДЕРЖИМОЕ: то же самое повторно — тот же ключ,
      // исправленное — новый.
      //
      // ЦИТАТА — ЧАСТЬ СОДЕРЖИМОГО. Без неё в подписи ломается ровно тот
      // случай, ради которого подпись и заведена: человек отправил «да»,
      // получил отказ, нажал «Ответить» на реплику куратора и отправил снова —
      // подпись та же, ключ тот же, и сервер вернул бы ПРЕЖНЕЕ сообщение, без
      // цитаты. Ответ потерялся бы молча.
      const replyUuid = this.replyTo?.message_uuid || '';
      const signature = `${replyUuid}|${this.text.trim()}|${uuids.join(',')}`;

      if (!this.draftKey || this.draftKeyFor !== signature) {
        this.draftKey = newKey();
        this.draftKeyFor = signature;
      }

      const res = await messenger.send(this.chatUuid, {
        clientMessageUuid: this.draftKey,
        text: this.text.trim() || null,
        attachmentUuids: uuids,
        replyTo: replyUuid || null,
      });

      this.sending = false;

      if (!res.ok) {
        // Текст писали руками — пропасть вместе с ошибкой он не должен.
        this.error = res.error || 'Сообщение не отправлено';
        return;
      }

      this.text = '';
      this.drafts = [];
      this.draftKey = '';
      this.draftKeyFor = '';

      // ПОСЛЕ ОТРИСОВКИ: grow() читает scrollHeight, а очищенное поле в этот
      // момент ещё содержит прежний текст — textarea оставался раздутым и
      // пустым на пол-экрана.
      this.$nextTick(() => this.grow());
      this.focusInput();
      this.$emit('sent', res.data);
    },
  },

  beforeUnmount() {
    this.cancelRecording();
  },
};
</script>

<style lang="scss" scoped>
/*
 * «Отвечаю на …» над полем ввода.
 *
 * Свой класс, а не `.msg-quote` из пузыря: та объявлена в scoped-блоке
 * MessageBubble и здесь просто не применилась бы — молча, без ошибки в
 * консоли. Цвета те же, что у остальной формы: #11415d — акцент кабинета.
 */
.msg-reply {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 5px 9px;
  border-left: 3px solid #11415d;
  border-radius: 0 9px 9px 0;
  background: #f3f5f7;

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__author {
    display: block;
    font-size: 11.5px;
    font-weight: 700;
    color: #11415d;
  }

  &__text {
    display: block;
    overflow: hidden;
    font-size: 12.5px;
    color: #6b7280;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /*
   * Крестик — управляющий элемент, а не подпись: #9ca3af на этой подложке даёт
   * 2.3:1, то есть ниже минимума даже для значка. И площадь касания: 14 пикселей
   * пальцем не берутся.
   */
  &__drop {
    flex: none;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: none;
    font-size: 15px;
    line-height: 1;
    color: #6b7280;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: #11415d;
    }

    &:focus-visible {
      outline: 2px solid #11415d;
      outline-offset: 1px;
    }
  }
}

.msg-composer {
  flex: none;
  padding: 12px 16px 14px;
  border-top: 1px solid #e8eaed;
  background: #fff;

  &__row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }

  &__input {
    flex: 1;
    min-width: 0;
    max-height: 140px;
    padding: 10px 12px;
    border: 1px solid #e8eaed;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.45;
    resize: none;
    outline: none;
    transition: border-color 0.15s;

    &:focus {
      border-color: #11415d;
    }
  }

  // Видимый фокус обязателен: без него человек с клавиатуры не понимает,
  // где находится, и жмёт наугад.
  &__attach:focus-visible,
  &__mic:focus-visible,
  &__send:focus-visible,
  &__input:focus-visible {
    outline: 2px solid #11415d;
    outline-offset: 2px;
  }

  &__attach,
  &__mic,
  &__send {
    flex: none;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    background: #f3f5f7;
    color: #6b7280;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #e8eaed;
      color: #111827;
    }
  }

  &__send {
    background: #11415d;
    color: #fff;

    &:hover:not(:disabled) {
      background: #1a5a7a;
      color: #fff;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: msg-spin 0.7s linear infinite;
  }

  &__drafts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }

  &__error {
    margin: 8px 0 0;
    font-size: 12.5px;
    color: #e8192c;
  }

  &__count {
    margin: 6px 0 0;
    text-align: right;
    font-size: 11.5px;
    color: #6b7280;
  }

  &__recording {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 4px;
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #e8192c;
    animation: msg-pulse 1s ease-in-out infinite;
  }

  &__timer {
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #111827;
  }

  &__hint {
    flex: 1;
    font-size: 12.5px;
    color: #6b7280;
  }

  &__cancel,
  &__stop {
    border: none;
    border-radius: 10px;
    padding: 7px 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  &__cancel {
    background: #f3f5f7;
    color: #6b7280;
  }

  &__stop {
    background: #11415d;
    color: #fff;
  }
}

.msg-draft {
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: 230px;
  padding: 5px 9px;
  border-radius: 9px;
  background: #f3f5f7;
  font-size: 12.5px;

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #111827;
  }

  &__bar {
    flex: none;
    width: 40px;
    height: 3px;
    border-radius: 2px;
    background: #d1d5db;
  }

  &__bar-fill {
    display: block;
    height: 100%;
    border-radius: 2px;
    background: #11415d;
    transition: width 0.2s;
  }

  &__drop {
    flex: none;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    font-size: 13px;
    line-height: 1;

    &:hover {
      color: #e8192c;
    }
  }
}

@keyframes msg-spin {
  to { transform: rotate(360deg); }
}

@keyframes msg-pulse {
  50% { opacity: 0.35; }
}

@media (max-width: 768px) {
  .msg-composer {
    padding: 10px 12px 12px;
  }
}
</style>
